import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AdminShell from "@/components/admin/AdminShell";
import api, { API, formatApiError } from "@/lib/api";
import { ArrowLeft, Upload, Save, Trash2, Move, CheckCircle2, ImageOff } from "lucide-react";

const TRIBES = [
  { key: "thrive_tribe", name: "Thrive Tribe" },
  { key: "nasal_ninja", name: "Nasal Ninja" },
  { key: "myo_princess", name: "Myo Princess" },
];
const LEVELS = Array.from({ length: 12 }, (_, i) => i + 1);

// Optional level titles the admin sees as a hint about which template belongs to which slot.
// These aren't rendered anywhere at runtime — the title lives inside the uploaded artwork.
const LEVEL_HINTS = {
  nasal_ninja: ["Ninja Recruit", "Ninja Apprentice", "Ninja Scout", "Ninja Cadet", "Ninja Guardian", "Ninja Sensei", "Ninja Warrior", "Ninja Elite", "Ninja Master", "Ninja Champion", "Ninja Legend", "Grand Ninja"],
  myo_princess: ["Princess of Breath", "Princess of Poise", "Princess of Grace", "Princess of Voice", "Princess of Strength", "Princess of Focus", "Princess of Wisdom", "Princess of Courage", "Princess of Kindness", "Princess of Radiance", "Princess of Legacy", "Grand Princess"],
  thrive_tribe: ["Tribe Newcomer", "Tribe Explorer", "Tribe Ally", "Tribe Companion", "Tribe Steward", "Tribe Champion", "Tribe Pathfinder", "Tribe Elder", "Tribe Guardian", "Tribe Leader", "Tribe Luminary", "Grand Tribe Elder"],
};

// -------- Positioning editor for one template --------

function PositionEditor({ template, onSave, onCancel }) {
  const [namePos, setNamePos] = useState(template.name_pos);
  const [datePos, setDatePos] = useState(template.date_pos);
  const [dragging, setDragging] = useState(null); // "name" | "date" | null
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [status, setStatus] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const pt = e.touches?.[0] || e;
      const x = Math.max(2, Math.min(98, ((pt.clientX - rect.left) / rect.width) * 100));
      const y = Math.max(2, Math.min(98, ((pt.clientY - rect.top) / rect.height) * 100));
      if (dragging === "name") setNamePos((p) => ({ ...p, x, y }));
      else setDatePos((p) => ({ ...p, x, y }));
    };
    const onUp = () => setDragging(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [dragging]);

  const save = async () => {
    setSaving(true); setErr(""); setStatus("");
    try {
      await api.put(`/admin/certificate-templates/${template.tribe}/${template.level}/positions`, { name_pos: namePos, date_pos: datePos });
      setStatus("Positions saved");
      onSave && onSave({ ...template, name_pos: namePos, date_pos: datePos });
      setTimeout(() => setStatus(""), 1600);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const sampleName = "Ellie Thompson";
  const sampleDate = "March 15, 2026";

  return (
    <div className="mt-4 rounded-2xl bg-[#0F1020] border border-white/10 p-4" data-testid={`cert-position-editor-${template.tribe}-${template.level}`}>
      <div className="flex items-center gap-2 mb-3">
        <Move size={14} className="text-amber-300" />
        <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-amber-300">Drag markers · Sample text</div>
      </div>
      <div
        ref={containerRef}
        className="relative w-full max-w-3xl mx-auto select-none rounded-xl overflow-hidden border border-white/10 bg-white"
        style={{ aspectRatio: template.aspect_ratio || 1.4, containerType: "inline-size" }}
      >
        <img src={template.image_data_url} alt="template" className="absolute inset-0 w-full h-full object-contain pointer-events-none" draggable={false} />
        {[
          { key: "name", pos: namePos, setPos: setNamePos, sample: sampleName, testid: `cert-pos-name-${template.tribe}-${template.level}` },
          { key: "date", pos: datePos, setPos: setDatePos, sample: sampleDate, testid: `cert-pos-date-${template.tribe}-${template.level}` },
        ].map((m) => (
          <div
            key={m.key}
            data-testid={m.testid}
            onMouseDown={(e) => { e.preventDefault(); setDragging(m.key); }}
            onTouchStart={(e) => { e.preventDefault(); setDragging(m.key); }}
            className="absolute cursor-move whitespace-nowrap px-2 py-0.5 rounded outline outline-2 outline-amber-300/60 hover:outline-amber-300"
            style={{
              left: `${m.pos.x}%`,
              top: `${m.pos.y}%`,
              transform: "translate(-50%, -50%)",
              fontSize: `${m.pos.font_size}cqw`,
              color: m.pos.color || "#2C2D5B",
              fontWeight: m.pos.font_weight || 700,
              fontFamily: m.pos.font_family || "Georgia, serif",
              background: dragging === m.key ? "rgba(251, 191, 36, 0.15)" : "transparent",
            }}
          >
            {m.sample}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
        {[
          { label: "Name marker", pos: namePos, setPos: setNamePos, testidBase: `cert-name-${template.tribe}-${template.level}` },
          { label: "Date marker", pos: datePos, setPos: setDatePos, testidBase: `cert-date-${template.tribe}-${template.level}` },
        ].map((row) => (
          <div key={row.label} className="rounded-xl bg-[#151737] border border-white/10 p-3">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-semibold mb-2">{row.label}</div>
            <div className="grid grid-cols-3 gap-2 text-[11px] text-slate-300">
              <label className="flex flex-col gap-1">
                x %
                <input type="number" step="0.5" min="0" max="100" value={row.pos.x} data-testid={`${row.testidBase}-x`}
                  onChange={(e) => row.setPos((p) => ({ ...p, x: parseFloat(e.target.value) || 0 }))}
                  className="px-2 py-1 rounded bg-[#0F1020] border border-white/10 text-white" />
              </label>
              <label className="flex flex-col gap-1">
                y %
                <input type="number" step="0.5" min="0" max="100" value={row.pos.y} data-testid={`${row.testidBase}-y`}
                  onChange={(e) => row.setPos((p) => ({ ...p, y: parseFloat(e.target.value) || 0 }))}
                  className="px-2 py-1 rounded bg-[#0F1020] border border-white/10 text-white" />
              </label>
              <label className="flex flex-col gap-1">
                font %
                <input type="number" step="0.1" min="0.5" max="20" value={row.pos.font_size} data-testid={`${row.testidBase}-size`}
                  onChange={(e) => row.setPos((p) => ({ ...p, font_size: parseFloat(e.target.value) || 1 }))}
                  className="px-2 py-1 rounded bg-[#0F1020] border border-white/10 text-white" />
              </label>
              <label className="flex flex-col gap-1 col-span-2">
                color
                <input type="color" value={row.pos.color || "#2C2D5B"} data-testid={`${row.testidBase}-color`}
                  onChange={(e) => row.setPos((p) => ({ ...p, color: e.target.value }))}
                  className="w-full h-8 rounded bg-[#0F1020] border border-white/10" />
              </label>
              <label className="flex flex-col gap-1">
                weight
                <select value={row.pos.font_weight || 700} data-testid={`${row.testidBase}-weight`}
                  onChange={(e) => row.setPos((p) => ({ ...p, font_weight: parseInt(e.target.value, 10) }))}
                  className="px-2 py-1 rounded bg-[#0F1020] border border-white/10 text-white">
                  {[400, 500, 600, 700, 800].map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </label>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <button type="button" onClick={save} disabled={saving} data-testid={`cert-save-positions-${template.tribe}-${template.level}`}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-amber-400 to-rose-400 text-[#0F1020] disabled:opacity-50">
          <Save size={13} /> {saving ? "Saving…" : "Save positions"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="text-[11px] text-slate-400 hover:text-white px-3 py-1">Close editor</button>
        )}
        {status && <span className="text-[11px] text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 size={11} /> {status}</span>}
        {err && <span className="text-[11px] text-rose-300">{err}</span>}
      </div>
    </div>
  );
}

// -------- Single-level row --------

function LevelRow({ tribe, level, summary, onChanged }) {
  const [full, setFull] = useState(null); // full template detail (with image_data_url)
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [showEditor, setShowEditor] = useState(false);
  const hint = LEVEL_HINTS[tribe]?.[level - 1] || `Level ${level}`;
  const has = !!summary;

  const loadDetail = async () => {
    setLoadingDetail(true); setErr("");
    try {
      const { data } = await api.get(`/admin/certificate-templates/${tribe}/${level}`);
      setFull(data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoadingDetail(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true); setErr("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/admin/certificate-templates/${tribe}/${level}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFull(data);
      setShowEditor(true);
      onChanged && await onChanged();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete the ${tribe.replace("_", " ")} · Level ${level} template? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/certificate-templates/${tribe}/${level}`);
      setFull(null); setShowEditor(false);
      onChanged && await onChanged();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const togglePosition = async () => {
    if (!showEditor && !full) await loadDetail();
    setShowEditor((v) => !v);
  };

  return (
    <div className="rounded-xl bg-[#0F1020] border border-white/10 p-4" data-testid={`cert-row-${tribe}-${level}`}>
      <div className="flex items-center gap-4 flex-wrap">
        <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center font-bold text-white text-sm shrink-0">L{level}</div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white">Level {level} · <span className="text-slate-300">{hint}</span></div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {has ? <>Uploaded {summary.updated_at ? new Date(summary.updated_at).toLocaleDateString() : ""} · aspect {summary.aspect_ratio?.toFixed?.(2) || summary.aspect_ratio || "—"}</> : "No template uploaded"}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold cursor-pointer bg-gradient-to-r from-amber-400 to-rose-400 text-[#0F1020]" data-testid={`cert-upload-label-${tribe}-${level}`}>
            <Upload size={11} /> {uploading ? "Uploading…" : has ? "Replace" : "Upload"}
            <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid={`cert-upload-input-${tribe}-${level}`} />
          </label>
          {has && (
            <button type="button" onClick={togglePosition} data-testid={`cert-toggle-position-${tribe}-${level}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold text-slate-200 bg-white/5 hover:bg-white/10 border border-white/10">
              <Move size={11} /> {showEditor ? "Close" : "Position"}
            </button>
          )}
          {has && (
            <button type="button" onClick={remove} data-testid={`cert-remove-${tribe}-${level}`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] text-rose-300 hover:text-rose-100 hover:bg-rose-500/10 border border-white/10">
              <Trash2 size={11} />
            </button>
          )}
        </div>
      </div>
      {err && <div className="mt-2 text-[11px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1">{err}</div>}
      {showEditor && (loadingDetail || !full) && <div className="mt-3 text-[11px] text-slate-400">Loading template…</div>}
      {showEditor && full && <PositionEditor template={full} onSave={(t) => { setFull(t); onChanged && onChanged(); }} onCancel={() => setShowEditor(false)} />}
    </div>
  );
}

// -------- Root page --------

export default function AdminCertificateTemplates() {
  const [tribe, setTribe] = useState("nasal_ninja");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/certificate-templates");
      setTemplates(data || []);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const byLevel = (t) => templates.find((x) => x.tribe === t.tribe && x.level === t.level);
  const rowsForTribe = LEVELS.map((level) => ({ tribe, level, summary: templates.find((x) => x.tribe === tribe && x.level === level) }));
  const uploadedCount = templates.filter((t) => t.tribe === tribe).length;

  return (
    <AdminShell subtitle="Content management" title="Certificate templates"
      right={<Link to="/admin/content" data-testid="cert-templates-back" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"><ArrowLeft size={14} /> Back to CMS</Link>}>
      <div className="rounded-2xl bg-[#151737] border border-white/10 p-4 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          {TRIBES.map((t) => {
            const count = templates.filter((x) => x.tribe === t.key).length;
            const isActive = tribe === t.key;
            return (
              <button key={t.key} type="button" onClick={() => setTribe(t.key)} data-testid={`cert-tribe-tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${isActive ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-slate-200"}`}>
                {t.name}
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-400/20 text-amber-300">{count}/12</span>
              </button>
            );
          })}
          <div className="ml-auto text-[11px] text-slate-500">Templates apply only to Thrive Tribe / Nasal Ninja / Myo Princess. Neutral & self-enrolled families keep the generic certificate.</div>
        </div>
      </div>

      {err && <div className="mb-4 rounded-lg text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 px-3 py-2">{err}</div>}

      {loading ? (
        <div className="text-slate-400 text-sm">Loading templates…</div>
      ) : (
        <div className="space-y-3" data-testid={`cert-templates-list-${tribe}`}>
          {rowsForTribe.map((row) => (
            <LevelRow key={`${row.tribe}-${row.level}`} tribe={row.tribe} level={row.level} summary={row.summary} onChanged={load} />
          ))}
        </div>
      )}
    </AdminShell>
  );
}
